import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component } from '@angular/core';
import { BackendAccessService } from '../../../../Services/APICalls/backend-access-service';
import { LoadingService } from '../../../../Services/Loading/loading-service.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OverturSkus } from '../../../../interfaces/overturSKU.interface';
import { debounceTime, filter, switchMap, tap } from 'rxjs';
import { OverturEndUserData } from '../../../../interfaces/overturEmailValidation.interface';
import { MatDialog } from '@angular/material/dialog';
import { SuccessDialogComponent } from '../../success-dialog/success-dialog.component';

@Component({
    selector: 'app-create-trial-page',
    templateUrl: './create-trial-page.component.html',
    styleUrls: ['./create-trial-page.component.scss'],
    animations: [
        trigger('slideDownInOut', [
            state('void', style({
                opacity: 0,
                transform: 'translateY(-10%)',
            })),
            transition('void => *', animate('600ms ease-out')),
            transition('* => void', animate('600ms ease-in')),
        ]),
    ],
    standalone: false
})
/* istanbul ignore next */
export class CreateTrialPageComponent {
  form: FormGroup;
  overturProducts: OverturSkus[] = [];
  planDescription: string = '';
  durations = [
    { value: 30, label: '30 Days' },
    { value: 60, label: '60 Days' },
    { value: 90, label: '90 Days' },
  ];
  emailValidationInProgress = false;
  emailValidationError: string | null = null;
  companyDetails: OverturEndUserData['endUserCompanyDetails'] | null = null;
  submissionError: string | null = null;

  constructor(
    private readonly backend: BackendAccessService,
    private readonly loading: LoadingService,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog
  ) {
    // Initialize form here or in ngOnInit
    this.form = this.fb.group({
      sku: ['', Validators.required],
      trialPeriodDays: ['', Validators.required],
      endUserEmail: ['', [Validators.required, Validators.email]],
      endUserCompanyAccountId: ['', Validators.required],
      endUserCompanyName: ['', Validators.required],
    });
  }
  /* istanbul ignore next */
  ngOnInit(): void {
    this.loading.show();
    this.setupEmailValidation();
    this.backend.getOverturSubSkus().subscribe({
      next: (data) => {
        this.overturProducts = data;
        console.log(this.overturProducts);
      },
      error: (err) => {
        console.error('Error fetching SKUs:', err);
        this.loading.hide();
      },
      complete: () => {
        console.log('Completed fetching SKUs');
        this.loading.hide();
      },
    });
    this.form.get('sku')?.valueChanges.subscribe((skuCode) => {
      this.updatePlanDetails(skuCode);
    });
    this.form.get('endUserEmail')?.valueChanges.subscribe(() => {
      if (this.submissionError) {
        this.submissionError = null;
      }
    });
  }
  /* istanbul ignore next */
  updatePlanDetails(skuCode: string) {
    console.log('SKU selected:', skuCode);
    const selectedProduct = this.overturProducts.find(
      (product) => product.skuCode === skuCode
    );
    console.log('Matched product:', selectedProduct);
    if (selectedProduct) {
      this.planDescription = selectedProduct.name;
    } else {
      this.planDescription = '';
    }
  }

  /* istanbul ignore next */
  setupEmailValidation() {
    this.form
      .get('endUserEmail')
      ?.valueChanges.pipe(
        debounceTime(500),
        tap((email) => {
          if (!email) {
            this.emailValidationInProgress = false;
            this.emailValidationError = null;
            this.companyDetails = null;
            this.form.patchValue(
              {
                endUserCompanyAccountId: '',
                endUserCompanyName: '',
              },
              { emitEvent: false }
            );
            const control = this.form.get('endUserEmail');
            if (control) {
              // Remove only custom invalidEmail error, keep required errors
              const errors = control.errors || {};
              if (errors['invalidEmail']) {
                delete errors['invalidEmail'];
                if (Object.keys(errors).length === 0) {
                  control.setErrors(null);
                } else {
                  control.setErrors(errors);
                }
                control.updateValueAndValidity({
                  onlySelf: true,
                  emitEvent: false,
                });
              }
            }
          }
        }),
        // Only proceed if email is not empty
        filter((email) => !!email),
        switchMap((email) => {
          this.emailValidationInProgress = true;
          this.emailValidationError = null;
          this.companyDetails = null;
          return this.backend.getOverturEmailValidation(email);
        })
      )
      .subscribe({
        next: (response: OverturEndUserData) => {
          this.emailValidationInProgress = false;
          if (response.errors && response.errors.length > 0) {
            this.emailValidationError = response.errors.join(', ');
            this.form.get('endUserEmail')?.setErrors({ invalidEmail: true });
            this.companyDetails = null;
            this.form.patchValue(
              {
                endUserCompanyAccountId: '',
                endUserCompanyName: '',
              },
              { emitEvent: false }
            );
          } else {
            this.emailValidationError = null;
            this.companyDetails = response.endUserCompanyDetails;
            if (this.companyDetails) {
              this.form.patchValue(
                {
                  endUserCompanyAccountId: this.companyDetails.accountId || '',
                  endUserCompanyName: this.companyDetails.businessId || '',
                },
                { emitEvent: false }
              );
            }
            // Clear custom error if present
            const control = this.form.get('endUserEmail');
            if (control) {
              const errors = control.errors || {};
              if (errors['invalidEmail']) {
                delete errors['invalidEmail'];
                if (Object.keys(errors).length === 0) {
                  control.setErrors(null);
                } else {
                  control.setErrors(errors);
                }
                control.updateValueAndValidity({
                  onlySelf: true,
                  emitEvent: false,
                });
              }
            }
          }
        },
        error: (err) => {
          this.emailValidationInProgress = false;
          this.emailValidationError = 'Error validating email.';
          this.companyDetails = null;
          this.form.patchValue(
            {
              endUserCompanyAccountId: '',
              endUserCompanyName: '',
            },
            { emitEvent: false }
          );
          console.error('Error validating email:', err);
        },
      });
  }

  /* istanbul ignore next */
  submitTrial() {
    if (this.form.valid) {
      this.loading.show();
      this.submissionError = null;
      this.backend.submitOverturAdminTrial(this.form.value).subscribe({
        next: (response: { isSuccess: boolean; errors?: string }) => {
          this.loading.hide();
          if (response.isSuccess) {
            this.dialog.open(SuccessDialogComponent, {
              width: '500px',
              disableClose: true,
            });
          } else {
            this.submissionError = response.errors ?? 'Unknown error occurred.';
          }
        },
        error: (err) => {
          this.loading.hide();
          this.submissionError =
            'Error creating trial. Please try again later.';
          console.error('Error creating trial:', err);
        },
      });
    } else {
      console.log('Form invalid');
      this.form.markAllAsTouched();
    }
  }
}
